import { useState, useEffect, useCallback } from 'react';
import { saveFocuses } from '../API';
import { CATEGORY_DEFINITIONS, FOCUS_LOOKUP } from '../../data/categoryDefinitions';
import './FocusSettings.css';

const CATEGORY_ORDER = ['performance', 'recovery', 'longevity', 'wellness'];

function getActiveCategoryId(focuses) {
    for (const fId of focuses) {
        const f = FOCUS_LOOKUP[fId];
        if (f) return f.categoryId;
    }
    return null;
}

export default function FocusSettings({ initialFocuses, onFocusesChange, isPremium = false }) {
    const [activeFocuses, setActiveFocuses] = useState([]);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(new Set());
    const [draftCategory, setDraftCategory] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!initialFocuses) return;
        setActiveFocuses(initialFocuses);
        setDraft(new Set(initialFocuses));
        setDraftCategory(getActiveCategoryId(initialFocuses));
    }, [initialFocuses]);

    const selectCategory = useCallback((catId) => {
        if (draftCategory === catId) return;
        setDraftCategory(catId);
        if (!isPremium) setDraft(new Set());
    }, [draftCategory, isPremium]);

    const toggleDraft = useCallback((focusId) => {
        setDraft(prev => {
            const next = new Set(prev);
            if (next.has(focusId)) {
                next.delete(focusId);
            } else if (isPremium || next.size < 3) {
                next.add(focusId);
            }
            return next;
        });
    }, [isPremium]);

    const handleSave = async () => {
        const arr = [...draft];
        setSaving(true);
        try {
            await saveFocuses(arr);
            setActiveFocuses(arr);
            setEditing(false);
            onFocusesChange?.(arr);
        } catch {
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setDraft(new Set(activeFocuses));
        setDraftCategory(getActiveCategoryId(activeFocuses));
        setEditing(false);
    };

    if (initialFocuses === null) return null;

    const activeCategoryId = getActiveCategoryId(activeFocuses);
    const activeCategoryName = activeCategoryId
        ? CATEGORY_DEFINITIONS[activeCategoryId]?.display_name
        : null;

    return (
        <div className="ns-root">
            <div className="ns-header">
                <svg className="ns-bell-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <circle cx="12" cy="12" r="3" />
                </svg>
                <h3 className="ns-title">Health Focuses</h3>
            </div>

            <div className="ns-card">
                {!editing ? (
                    <>
                        {activeFocuses.length === 0 ? (
                            <p className="fs-empty">No focuses selected yet.</p>
                        ) : (
                            <>
                                {activeCategoryName && (
                                    <div className="fs-category-label">{activeCategoryName}</div>
                                )}
                                <div className="fs-pills">
                                    {activeFocuses.map(fId => {
                                        const f = FOCUS_LOOKUP[fId];
                                        if (!f) return null;
                                        return (
                                            <span
                                                key={fId}
                                                className="fs-pill"
                                                style={{ backgroundColor: f.color, color: '#fff' }}
                                            >
                                                {f.display_name}
                                            </span>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <button className="fs-edit-btn" onClick={() => setEditing(true)}>
                            {activeFocuses.length === 0 ? 'Choose Focuses' : 'Edit Focuses'}
                        </button>
                    </>
                ) : (
                    <div className="fs-editor">
                        <div className="fs-cat-selector">
                            {CATEGORY_ORDER.map(catId => {
                                const cat = CATEGORY_DEFINITIONS[catId];
                                const isActive = draftCategory === catId;
                                return (
                                    <button
                                        key={catId}
                                        className={`fs-cat-btn ${isActive ? 'fs-cat-btn-active' : ''}`}
                                        style={isActive ? { borderColor: cat.color, color: cat.color } : undefined}
                                        onClick={() => selectCategory(catId)}
                                    >
                                        {cat.display_name}
                                    </button>
                                );
                            })}
                        </div>

                        {draftCategory && (
                            <>
                                <div className="fs-count">{isPremium ? `${draft.size} selected` : `${draft.size}/3 selected`}</div>
                                <div className="fs-category">
                                    {Object.entries(CATEGORY_DEFINITIONS[draftCategory].focuses).map(([fId, focus]) => {
                                        const checked = draft.has(fId);
                                        const disabled = !checked && !isPremium && draft.size >= 3;
                                        return (
                                            <label
                                                key={fId}
                                                className={`fs-focus-row ${disabled ? 'fs-disabled' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    disabled={disabled}
                                                    onChange={() => toggleDraft(fId)}
                                                />
                                                <span className="fs-focus-label">{focus.display_name}</span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        <div className="fs-actions">
                            <button className="fs-cancel" onClick={handleCancel}>Cancel</button>
                            <button
                                className="fs-save"
                                onClick={handleSave}
                                disabled={saving || draft.size === 0}
                            >
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
